import React from "react";
import { useTranslation } from "react-i18next";
import Moment from "react-moment";
import { Box, SxProps, Theme } from "@mui/material";
import { EventType, FixtureTimelineEvent } from "../../../models/fixture";
import { FixtureDetail } from "../../../models/fixturedetail";
import { SlimCouponRowStyles } from "../../../slimcoupon/slimfixturedetail";

interface MatchEventsProps {
  fixtureDetails: FixtureDetail;
  isRightToLeft: boolean;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const isCenteredEvent = (type: EventType): boolean =>
  type === "HALF_TIME" ||
  type === "FULL_TIME" ||
  type === "KICKOFF" ||
  type === "FINISHED_AFTER_EXTRA";

const getEventToken = (eventType: EventType): string => {
  switch (eventType) {
    case "GOAL":
      return "event_goal";
    case "PENALTY":
      return "event_penalty";
    case "MISSED_PENALTY":
      return "event_missed_penalty";
    case "OWN_GOAL":
      return "event_own_goal";
    case "VAR":
      return "event_var";
    case "YELLOW_CARD":
      return "event_yellowcard";
    case "YELLOW_RED":
      return "event_yellowred";
    case "SUBSTITUTION":
      return "event_substitution";
    case "RED_CARD":
      return "event_redcard";
    case "PEN_SHOOTOUT_GOAL":
      return "event_pen_shootout_goal";
    case "PEN_SHOOTOUT_MISS":
      return "event_pen_shootout_miss";
    case "FULL_TIME":
      return "event_full_time";
    case "HALF_TIME":
      return "event_half_time";
    case "KICKOFF":
      return "event_kickoff";
    case "FINISHED_AFTER_EXTRA":
      return "event_finished_after_extra";
    default:
      return "";
  }
};

const EventIcon: React.FC<{ type: EventType }> = ({ type }) => {
  const iconSx: SxProps<Theme> = { fontSize: "14px", lineHeight: "14px" };
  switch (type) {
    case "GOAL":
    case "PENALTY":
      return <Box component="span" className="icon-football" sx={{ ...iconSx, color: "var(--text-black)" }} />;
    case "OWN_GOAL":
    case "MISSED_PENALTY":
      return <Box component="span" className="icon-football" sx={{ ...iconSx, color: "#d40202" }} />;
    case "YELLOW_CARD":
      return <Box component="span" className="icon-card-1" sx={{ ...iconSx, color: "#ecc434" }} />;
    case "RED_CARD":
    case "YELLOW_RED":
      return <Box component="span" className="icon-card-1" sx={{ ...iconSx, color: "#d40202" }} />;
    case "SUBSTITUTION":
      return <Box component="span" className="icon-arrows" sx={{ ...iconSx, color: "var(--text-black)" }} />;
    default:
      return null;
  }
};

/* ------------------------------------------------------------------ */
/*  Styles                                                            */
/* ------------------------------------------------------------------ */

const rowBaseSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
  minHeight: "32px",
  padding: "4px 8px",
  boxSizing: "border-box",
};

const minuteSx: SxProps<Theme> = {
  width: "40px",
  minWidth: "40px",
  flexShrink: 0,
  fontSize: "12px",
  fontWeight: "bold",
  color: "var(--font-color-supportive)",
  textAlign: "left",
};

const sideSx = (align: "flex-start" | "flex-end"): SxProps<Theme> => ({
  flex: 1,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: align,
  gap: "6px",
  minWidth: 0,
});

const playerSx: SxProps<Theme> = {
  fontSize: "12px",
  fontWeight: 500,
  color: "var(--text-black)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const centerSx: SxProps<Theme> = {
  minWidth: "44px",
  textAlign: "center",
  fontSize: "12px",
  fontWeight: "bold",
  color: "var(--text-black)",
  flexShrink: 0,
};

/* ------------------------------------------------------------------ */

const MatchEvents: React.FC<MatchEventsProps> = ({ fixtureDetails, isRightToLeft }) => {
  const { t } = useTranslation();
  const { fixture } = fixtureDetails;
  const homeTeamId = fixture.homeTeam.id;

  if (!fixture.fixtureTimelineEvents) {
    return <Box />;
  }

  // Data arrives latest-first; show chronological (earliest-first) like LiveScore.
  const events = fixture.fixtureTimelineEvents
    .filter((e) => e.type !== "UNKNOWN")
    .slice()
    .reverse();

  // Track the running score so HT/FT rows can show it.
  let lastResult = "0 - 0";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", maxWidth: "600px", margin: "8px auto" }}>
      {events.map((event, i) => {
        if (event.result) {
          lastResult = isRightToLeft
            ? event.result.split("").reverse().join("")
            : event.result;
        }

        const rowSx: SxProps<Theme> = {
          ...(i % 2 === 0 ? SlimCouponRowStyles.rowEven : SlimCouponRowStyles.rowOdd),
          ...rowBaseSx,
        } as SxProps<Theme>;

        const centered = isCenteredEvent(event.type);

        if (centered) {
          const startingAt = new Date(fixture.startingAt);
          const minuteLabel =
            event.type === "HALF_TIME"
              ? "HT"
              : event.type === "FULL_TIME"
                ? "FT"
                : event.type === "FINISHED_AFTER_EXTRA"
                  ? "AET"
                  : "";

          return (
            <Box key={`event ${i}`} sx={rowSx}>
              <Box sx={minuteSx}>{minuteLabel}</Box>
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                <Box sx={centerSx}>
                  {event.type === "KICKOFF" ? (
                    <>
                      {t(getEventToken(event.type))}{" "}
                      <Moment format={"HH:mm"} locale={t("locale")}>
                        {startingAt}
                      </Moment>
                    </>
                  ) : (
                    lastResult
                  )}
                </Box>
              </Box>
            </Box>
          );
        }

        const isHomeEvent = event.team ? homeTeamId === event.team.id : false;
        const eventLeft = (isHomeEvent && !isRightToLeft) || (!isHomeEvent && isRightToLeft);

        const minuteLabel = `${event.minute ?? ""}${
          event.extra_minute ? `+${event.extra_minute}` : ""
        }${event.minute ? "'" : ""}`;

        const isGoal =
          event.type === "GOAL" ||
          event.type === "PENALTY" ||
          event.type === "OWN_GOAL";

        const playerContent = (
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Box sx={playerSx}>{event.player_name}</Box>
            {event.type === "SUBSTITUTION" && event.related_player_name && (
              <Box sx={{ fontSize: "10px", color: "var(--grey-out-50-percent)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {t("out")}: {event.related_player_name}
              </Box>
            )}
          </Box>
        );

        return (
          <Box key={`event ${i}`} sx={rowSx}>
            <Box sx={minuteSx}>{minuteLabel}</Box>

            {/* Left side */}
            <Box sx={sideSx("flex-end")}>
              {eventLeft && (
                <>
                  {playerContent}
                  <EventIcon type={event.type} />
                </>
              )}
            </Box>

            {/* Center score */}
            <Box sx={centerSx}>{isGoal ? lastResult : ""}</Box>

            {/* Right side */}
            <Box sx={sideSx("flex-start")}>
              {!eventLeft && (
                <>
                  <EventIcon type={event.type} />
                  {playerContent}
                </>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default MatchEvents;
